import { createFileRoute } from "@tanstack/react-router";
import { organizationSchemaScripts } from "@/lib/org-schema";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";

import { Header } from "@/components/landing/Header";
import { Hero } from "@/components/landing/Hero";
import { LogoStrip } from "@/components/landing/LogoStrip";
import { ParticleBackground } from "@/components/landing/ParticleBackground";

import { FutureSection } from "@/components/landing/FutureSection";
import { LeadsInboxSection } from "@/components/landing/LeadsInboxSection";
import { ServicesSection } from "@/components/landing/ServicesSection";
import { SquarespaceSection } from "@/components/landing/SquarespaceSection";
import { EventLandingPagesSection } from "@/components/landing/EventLandingPagesSection";
import { CustomBuildsSection } from "@/components/landing/CustomBuildsSection";
import { TestimonialsSection } from "@/components/landing/TestimonialsSection";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { Footer } from "@/components/landing/Footer";
import heroRoiAsset from "@/assets/hero-roi-calculator.png.asset.json";

const TITLE = "Lead Generation Tools & Website Modernization | Square Migration";
const DESCRIPTION =
  "Lead generation tools and website modernization for B2B marketing teams. We build calculators, assessments, and interactive apps, plus modern Squarespace websites that convert.";
const PUBLISHED = "2026-05-29T00:00:00+00:00";
const MODIFIED = "2026-09-03T00:00:00+00:00";

const FAQS: FaqItem[] = [
  {
    question: "What is an interactive lead generation tool?",
    answer:
      "It's a small web app that gives a prospect a real answer instead of a PDF, an ROI calculator, a readiness assessment, a scanner or a configurator. The visitor gets a result they care about, and you get a qualified lead with the inputs they used to get there.",
  },
  {
    question: "How long does a lead generation tool take to build?",
    answer:
      "Most single-purpose calculators and assessments ship in weeks, not quarters. We scope the one question the tool has to answer, design it, and get a working version live so you can start collecting leads while we refine it.",
  },
  {
    question: "Will it work with my existing website and CRM?",
    answer:
      "Yes. Tools are built to live on your domain and can be embedded in or linked from your current site, including Squarespace. Submissions can be routed to your CRM or marketing platform so leads land where your team already works.",
  },
  {
    question: "Do you also build full custom applications?",
    answer:
      "Yes. When something needs accounts, a database, roles or an admin area, it becomes a product build rather than a lightweight tool, sales enablement apps, lead qualifiers, internal dashboards and client-facing platforms.",
  },
  {
    question: "Do you work on Squarespace websites too?",
    answer:
      "We design and modernize Squarespace sites and landing pages, including event and RSVP pages, so the site around your tools converts just as well as the tools themselves.",
  },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "google-site-verification", content: "I3A56oJATi5r77zUReRsS4bZNGpKf8qOta08-_-9xHA" },
      { name: "google-site-verification", content: "F14--DwUBsrX1Vz5tSScXfhM8XiK7RWLY7AqFekfTNw" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [
      { rel: "canonical", href: "https://www.squaremigration.com" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" },
      { rel: "preload", as: "image", href: heroRoiAsset.url, fetchPriority: "high" },
    ],
    scripts: [
      ...organizationSchemaScripts(),
      faqSchemaScript(FAQS),
      webPageSchemaScript({
        path: "/",
        name: TITLE,
        description: DESCRIPTION,
        published: PUBLISHED,
        modified: MODIFIED,
      }),
    ],

  }),
  component: Index,
});

function Index() {
  return (
    <main className="relative">
      {/* Rendered here because the router dedupes meta tags by name; React 19 hoists it into <head>. */}
      <meta name="google-site-verification" content="I3A56oJATi5r77zUReRsS4bZNGpKf8qOta08-_-9xHA" />
      <ParticleBackground />
      <Header />
      <Hero />
      <LogoStrip />
      <FutureSection />
      <LeadsInboxSection />
      <ServicesSection />
      <SquarespaceSection />
      <EventLandingPagesSection showExamples={false} />
      <CustomBuildsSection summary />
      <TestimonialsSection />
      <FaqSection title="Questions we hear before a build" items={FAQS} />
      <ClosingCTA />

      <Footer />
    </main>
  );
}
